import { useEffect } from "react";
import { flushPendingPaidConversion } from "@/lib/analytics";
import { isRealPaddleSubscriptionId, peekPendingPaidConversion } from "@/lib/paidConversionTracking";
import { useSessionQuery } from "@/queries/queries";
import { useSubscription } from "@/queries/subscription-queries";

/**
 * After Paddle checkout redirects home, the webhook may have replaced `"temp"`
 * with a real `sub_…`. Flush the pending `purchase` conversion once that lands.
 * Analytics failures are swallowed — this must never affect the app shell.
 */
export function PaidConversionTracker() {
    const { data: sessionData } = useSessionQuery();
    const { data: subscriptionData, refetch } = useSubscription();

    useEffect(() => {
        flushPendingPaidConversion({
            subscription: subscriptionData?.subscription ?? null,
            email: sessionData?.user?.email,
            organizationId: sessionData?.session?.activeOrganizationId,
        });

        let storage: Storage | null = null;
        try {
            storage = window.sessionStorage;
        } catch {
            return;
        }
        const pending = peekPendingPaidConversion(storage);
        if (!pending) {
            return;
        }
        if (isRealPaddleSubscriptionId(subscriptionData?.subscription?.paddleSubscriptionId)) {
            return;
        }

        const interval = window.setInterval(() => {
            void refetch();
        }, 1000);
        const stop = window.setTimeout(() => window.clearInterval(interval), 15_000);
        return () => {
            window.clearInterval(interval);
            window.clearTimeout(stop);
        };
    }, [
        refetch,
        subscriptionData?.subscription?.paddleSubscriptionId,
        subscriptionData?.subscription?.isActive,
        subscriptionData?.subscription?.status,
        sessionData?.user?.email,
        sessionData?.session?.activeOrganizationId,
    ]);

    return null;
}
